<template>
    <v-app-bar app dense class="orange accent-3">
        <v-btn v-if="back" icon dark @click="goBack">
            <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-icon v-else dark>mdi-bus</v-icon>

        <v-toolbar-title class="white--text subtitle-1 ml-2">
            {{ plate || 'Delivery Kiosk' }}
        </v-toolbar-title>

        <v-icon v-if="sharing" dark small class="ml-2">mdi-crosshairs-gps</v-icon>
        <span v-if="sharing" class="white--text caption ml-1">กำลังแชร์ตำแหน่ง</span>

        <v-spacer></v-spacer>

        <v-menu offset-y bottom left>
            <template v-slot:activator="{ on }">
                <v-btn icon dark v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn>
            </template>
            <v-list dense>
                <!-- Who this phone is signed in as. Every stamp is recorded in
                     this name, so a phone left signed in as someone else is
                     caught here rather than in a report. -->
                <v-subheader v-if="driverName" class="black--text font-weight-bold">
                    <v-icon small class="mr-2">mdi-account</v-icon>{{ driverName }}
                </v-subheader>
                <v-divider v-if="driverName"></v-divider>
                <v-list-item :to="{ name: 'driverCar' }">
                    <v-list-item-title>เปลี่ยนรถ</v-list-item-title>
                </v-list-item>
                <v-list-item :to="{ name: 'driverStation' }">
                    <v-list-item-title>แจ้งสถานีชาร์จ/ปั๊มแก๊ส</v-list-item-title>
                </v-list-item>
                <v-divider></v-divider>
                <v-list-item :disabled="loggingOut" @click="logout">
                    <v-list-item-title>ออกจากระบบ</v-list-item-title>
                </v-list-item>
                <v-divider></v-divider>
                <!-- Which build this phone runs, so a driver on the phone with
                     the office can read it out. -->
                <v-subheader class="caption grey--text">{{ versionText }}</v-subheader>
            </v-list>
        </v-menu>

        <!-- A newer build is live. Offered, never forced: a reload in the
             middle of a photo sheet or a signature would lose it. -->
        <template v-if="updateReady" v-slot:extension>
            <div class="d-flex align-center white--text body-2" style="width: 100%">
                <v-icon dark small class="mr-2">mdi-update</v-icon>
                <span>มีเวอร์ชันใหม่ ทำงานที่ค้างให้เสร็จก่อน</span>
                <v-spacer></v-spacer>
                <v-btn small light color="white" class="orange--text text--darken-3" @click="reload">โหลดใหม่</v-btn>
            </div>
        </template>
    </v-app-bar>
</template>
<script>
import driverService from '@/components/driverService'
import V from '@/components/appVersion'

// Shared by every mount of the bar: each driver screen draws its own, and
// moving between them should not fetch version.json again each time.
const versionState = { lastCheckedAt: 0, updateReady: false }

export default {
    // `back` is a route name because an installed PWA on iOS has no browser
    // back button -- every screen has to carry its own.
    props: {
        plate: { type: String, default: '' },
        back: { type: String, default: '' }
    },
    data () {
        return {
            // A second tap while signOutDriver() is still awaiting would run the
            // whole sign-out again and fire a second push at the keypad.
            loggingOut: false,
            // Drivers are not tracked silently. This also doubles as the
            // diagnosis when the board shows a truck as stale: if the driver can
            // see the indicator is off, they know why.
            sharing: driverService.isPinging(),
            sharingTimer: null,
            driverName: '',
            updateReady: versionState.updateReady,
            versionTimer: null
        }
    },
    computed: {
        versionText () {
            return V.versionLabel(V.CURRENT.version)
        }
    },
    mounted () {
        // Read from the token's claims (driverService repairs the Thai there).
        // A failure only leaves the name off the menu -- never worth an error.
        driverService.ensureDriverClaims().then(c => {
            if (!c) return
            this.driverName = c.driverName
        }).catch(() => {})
        // Polled rather than pushed: the bar is mounted by every driver screen
        // and the arming happens in a service with no event bus. One cheap
        // boolean read every few seconds is less machinery than wiring one up.
        this.sharingTimer = setInterval(() => { this.sharing = driverService.isPinging() }, 5000)
        // Checked when a screen opens, when the app comes back to the
        // foreground (an installed PWA is usually resumed, not reopened), and
        // every few minutes while it stays open; checkDue spaces them out.
        this.checkVersion()
        this.versionTimer = setInterval(this.checkVersion, V.CHECK_GAP_MS)
        document.addEventListener('visibilitychange', this.checkVersion)
    },
    beforeDestroy () {
        if (this.sharingTimer) clearInterval(this.sharingTimer)
        if (this.versionTimer) clearInterval(this.versionTimer)
        document.removeEventListener('visibilitychange', this.checkVersion)
    },
    methods: {
        // Both pushes are raced by App.vue's auth listener, which sends a
        // signed-out driver to driverPin itself. Whichever loses the race gets a
        // NavigationDuplicated rejection -- a real unhandled promise rejection in
        // vue-router 3, not a warning -- so both are caught. There is nothing to
        // recover: the navigation the user asked for has already happened.
        goBack () {
            this.$router.push({ name: this.back }).catch(() => {})
        },
        async checkVersion () {
            if (document.visibilityState === 'hidden' || versionState.updateReady) return
            const now = Date.now()
            if (!V.checkDue(versionState.lastCheckedAt, now)) return
            versionState.lastCheckedAt = now
            const live = await V.fetchLiveVersion()
            if (V.updateAvailable(V.CURRENT.build, live)) versionState.updateReady = true
            this.updateReady = versionState.updateReady
        },
        reload () {
            window.location.reload()
        },
        async logout () {
            if (this.loggingOut) return
            this.loggingOut = true
            try {
                await driverService.signOutDriver()
                this.$router.push({ name: 'driverPin' }).catch(() => {})
            } finally {
                this.loggingOut = false
            }
        }
    }
}
</script>
